import { useEffect } from 'react';

/**
 * Recharge l'écran quand le serveur sert un front PLUS RÉCENT que celui en cours.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * POURQUOI : un écran d'affichage reste allumé des semaines. Quand le serveur est réinstallé,
 * la page déjà chargée ne se recharge pas — aucune navigation n'a lieu, la WebView garde en
 * mémoire le code d'avant, et le socket se reconnecte comme si de rien n'était. On croit la
 * mise à jour faite ; l'écran tourne encore sur l'ancien front, sans le moindre signe. C'est
 * arrivé, et ça ne se voit qu'en constatant qu'un correctif « n'a rien changé ».
 *
 * COMMENT : la page lit le nom de SON propre paquet dans le DOM, et le compare à celui que le
 * serveur annonce. Le nom porte une empreinte du contenu — il change exactement quand le front
 * change, jamais à un simple redémarrage. Aucune injection à la compilation n'est nécessaire :
 * la page sait ce qu'elle exécute, le serveur sait ce qu'il sert.
 *
 * CE QUI NE DÉCLENCHE RIEN, et c'est délibéré : une coupure réseau, un redémarrage du serveur,
 * une réponse vide ou illisible. Un écran de salle d'attente qui se recharge en boucle serait
 * pire que le mal — on ne recharge que sur un écart AVÉRÉ entre deux noms non vides.
 */

/** Nom du paquet principal RÉELLEMENT exécuté, lu depuis la balise qui l'a chargé. */
function paquetCourant() {
  const balise = [...document.querySelectorAll('script[src]')]
    .map((s) => s.getAttribute('src') || '')
    .find((src) => /\/static\/js\/main\.[^/]+\.js$/.test(src));
  return balise ? balise.split('/').pop() : '';
}

export function useFrontVersion({ intervalMs = 5 * 60 * 1000 } = {}) {
  useEffect(() => {
    const mien = paquetCourant();
    if (!mien) return; // développement (bundle non haché) : rien à comparer

    let vivant = true;

    const verifier = async () => {
      try {
        const r = await fetch('/api/app-version', { cache: 'no-store' });
        const j = await r.json();
        const servi = j?.data?.main || '';
        if (!vivant || !servi || servi === mien) return;

        // Garde-fou : si le rechargement ne réglait pas l'écart — cache intermédiaire,
        // proxy qui sert une version figée — l'écran boucleroit indéfiniment. On ne
        // retente pas la même bascule avant une heure.
        const cle = `tf-front-reload:${servi}`;
        const dernier = Number(sessionStorage.getItem(cle) || 0);
        if (Date.now() - dernier < 3600000) return;
        sessionStorage.setItem(cle, String(Date.now()));

        console.warn(`[front] nouvelle version servie (${servi} ≠ ${mien}) — rechargement de l'écran`);
        window.location.reload();
      } catch {
        /* serveur injoignable : on ne conclut rien, on retentera */
      }
    };

    verifier();
    const t = setInterval(verifier, intervalMs);
    return () => { vivant = false; clearInterval(t); };
  }, [intervalMs]);
}

export default useFrontVersion;
